import { PackageSearch, SlidersHorizontal, X } from "lucide-react";
import Link from "next/link";
import { Suspense, useEffect, useState } from "react";

import { ProductGrid } from "@/components/product/product-grid";
import { SearchSidebar } from "@/components/product/search-sidebar";
import { SortSelect } from "@/components/product/sort-select";
import { useSearchParamsObject } from "@/hooks/use-search-params-object";
import { getCategories, queryProducts, type ProductPage } from "@/lib/queries";
import type { SearchParams } from "@/types";

/** `app/search/page.tsx`. */
export function SearchPage() {
  const params = useSearchParamsObject();

  const [categories, setCategories] = useState<string[]>([]);
  const [result, setResult] = useState<ProductPage | null>(null);
  const [mobileFilterOpen, setMobileFilterOpen] = useState(false);

  const activeFiltersCount =
    (params.category && params.category !== "All" ? 1 : 0) +
    (params.collection ? 1 : 0) +
    (params.rating ? 1 : 0) +
    (params.prime ? 1 : 0);

  const queryKey = JSON.stringify(params);

  useEffect(() => {
    let active = true;
    void getCategories().then((value) => {
      if (active) setCategories(value);
    });
    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    let active = true;
    void queryProducts(params as SearchParams, 12).then((page) => {
      if (active) setResult(page);
    });
    return () => {
      active = false;
    };
    // `queryKey` is the serialised query string: this re-runs on navigation
    // while keeping a stable dependency (`params` is a new object each render).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queryKey]);

  function pageHref(nextPage: number) {
    const next = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value) next.set(key, String(value));
    });
    next.set("page", String(nextPage));
    return `/search?${next.toString()}`;
  }

  if (!result) {
    return (
      <div className="mx-auto max-w-[1500px] px-3 py-4 sm:px-4">
        <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
          {Array.from({ length: 8 }).map((_, i) => (
            <div key={i} className="h-64 animate-pulse rounded-lg bg-slate-100 dark:bg-white/5" />
          ))}
        </div>
      </div>
    );
  }

  const { items, total, page, totalPages } = result;

  return (
    <div className="mx-auto max-w-[1500px] px-3 py-6 sm:px-4">
      <div className="mb-4 rounded-2xl border border-slate-200/80 bg-white px-5 py-3.5 shadow-xs dark:border-white/10 dark:bg-slate-900">
        <p className="text-sm text-slate-600 dark:text-slate-300">
          {total > 0 ? (
            <>
              <span className="font-bold text-[#073B70] dark:text-white">{total}</span> result{total === 1 ? "" : "s"}
              {params.q ? (
                <>
                  {" "}
                  for <span className="font-bold text-[#073B70] dark:text-white">&ldquo;{params.q}&rdquo;</span>
                </>
              ) : null}
              {params.category && params.category !== "All" ? (
                <>
                  {" "}
                  in <span className="font-bold text-[#073B70] dark:text-white">{params.category}</span>
                </>
              ) : null}
              {params.collection ? (
                <>
                  {" "}
                  in collection <span className="font-bold text-[#073B70] dark:text-white">&ldquo;{params.collection}&rdquo;</span>
                </>
              ) : null}
            </>
          ) : (
            "No results found"
          )}
        </p>
      </div>

      <div className="flex flex-col gap-6 lg:flex-row">
        {/* Desktop Sidebar */}
        <div className="hidden lg:block w-60 shrink-0">
          <SearchSidebar params={params} initialCategories={categories} />
        </div>

        {/* Mobile Filters Drawer */}
        {mobileFilterOpen && (
          <div className="fixed inset-0 z-50 flex lg:hidden" role="dialog" aria-modal="true" aria-label="Filters">
            <div
              className="fixed inset-0 bg-black/50 backdrop-blur-xs transition-opacity"
              onClick={() => setMobileFilterOpen(false)}
            />
            <div className="relative z-10 flex h-full w-[85%] max-w-xs flex-col bg-white shadow-xl dark:bg-slate-900">
              <div className="flex items-center justify-between border-b border-slate-200 px-4 py-3.5 dark:border-white/10">
                <div className="flex items-center gap-2">
                  <SlidersHorizontal className="h-4 w-4 text-[#087DB5]" />
                  <h2 className="text-base font-black text-[#073B70] dark:text-white">Filters</h2>
                  {activeFiltersCount > 0 && (
                    <span className="flex h-5 w-5 items-center justify-center rounded-full bg-[#FF8A00] text-[10px] font-black text-white">
                      {activeFiltersCount}
                    </span>
                  )}
                </div>
                <button
                  type="button"
                  onClick={() => setMobileFilterOpen(false)}
                  className="rounded-lg p-1.5 text-slate-500 hover:bg-slate-100 dark:text-slate-400 dark:hover:bg-white/10"
                  aria-label="Close filters"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>

              <div className="flex-1 overflow-y-auto p-4">
                <SearchSidebar
                  params={params}
                  initialCategories={categories}
                  onFilterApplied={() => setMobileFilterOpen(false)}
                  className="w-full"
                />
              </div>

              <div className="border-t border-slate-200 p-3.5 dark:border-white/10">
                <button
                  type="button"
                  onClick={() => setMobileFilterOpen(false)}
                  className="w-full rounded-xl bg-gradient-to-r from-[#087DB5] to-[#073B70] py-3 text-center text-sm font-bold text-white shadow-sm hover:brightness-110"
                >
                  Show {total} Results
                </button>
              </div>
            </div>
          </div>
        )}

        <div className="min-w-0 flex-1 space-y-4">
          <div className="flex items-center justify-between gap-2">
            <button
              type="button"
              onClick={() => setMobileFilterOpen(true)}
              className="flex items-center gap-2 rounded-xl border border-slate-300 bg-white px-3.5 py-2 text-sm font-bold text-[#073B70] shadow-2xs hover:border-[#087DB5] lg:hidden dark:border-white/15 dark:bg-slate-900 dark:text-slate-200"
            >
              <SlidersHorizontal className="h-4 w-4 text-[#087DB5]" />
              <span>Filters</span>
              {activeFiltersCount > 0 && (
                <span className="flex h-5 w-5 items-center justify-center rounded-full bg-[#FF8A00] text-[10px] font-black text-white">
                  {activeFiltersCount}
                </span>
              )}
            </button>

            <div className="ml-auto">
              <Suspense fallback={<div className="h-9 w-40" />}>
                <SortSelect />
              </Suspense>
            </div>
          </div>

          {items.length > 0 ? (
            <ProductGrid products={items} />
          ) : (
            <div className="rounded-2xl border border-slate-200/80 bg-white p-12 text-center shadow-xs dark:border-white/10 dark:bg-slate-900">
              <PackageSearch className="mx-auto h-12 w-12 text-[#087DB5]/50" />
              <h2 className="mt-3 text-xl font-black text-[#073B70] dark:text-white">No products found</h2>
              <p className="mt-2 text-sm text-slate-600 dark:text-slate-300">Try a broader search term or clear active filters.</p>
            </div>
          )}

          {totalPages > 1 && (
            <div className="flex items-center justify-center gap-3 pt-4">
              <Link
                href={pageHref(Math.max(1, page - 1))}
                aria-disabled={page <= 1}
                className="rounded-xl border border-slate-300 bg-white px-4 py-2 text-sm font-bold text-[#073B70] transition hover:border-[#087DB5] hover:bg-slate-50 aria-disabled:pointer-events-none aria-disabled:opacity-40 dark:border-white/10 dark:bg-slate-900 dark:text-white"
              >
                Previous
              </Link>
              <span className="text-sm font-bold text-slate-600 dark:text-slate-300">
                Page {page} of {totalPages}
              </span>
              <Link
                href={pageHref(Math.min(totalPages, page + 1))}
                aria-disabled={page >= totalPages}
                className="rounded-xl border border-slate-300 bg-white px-4 py-2 text-sm font-bold text-[#073B70] transition hover:border-[#087DB5] hover:bg-slate-50 aria-disabled:pointer-events-none aria-disabled:opacity-40 dark:border-white/10 dark:bg-slate-900 dark:text-white"
              >
                Next
              </Link>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}